import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, getDoc, updateDoc, addDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase'; // Your Firebase config
import './AssignDelivery.css';

// OrderCard component remains a good way to structure the UI
const OrderCard = ({ order, deliveryPersons, onAssign }) => {
    const [selectedPerson, setSelectedPerson] = useState('');

    const handleAssignClick = () => {
        if (!selectedPerson) {
            alert("Please select a delivery person.");
            return;
        }
        onAssign(order, selectedPerson); // Pass the entire order object
    };

    return (
        <div className="order-card">
            <h3>Order for: {order.customerName}</h3>
            <div className="order-details">
                <p><strong>Address:</strong> {order.customer.address || 'N/A'}</p>
                <p><strong>Phone:</strong> {order.customer.phone || 'N/A'}</p>
                <p><strong>Location:</strong> {order.customer.location || 'N/A'}</p>
            </div>
            <div className="package-details">
                <h4>Package: {order.packageName}</h4>
                {order.foodItems && order.foodItems.length > 0 ? (
                    <div>
                        <p><strong>Today's {order.mealType.charAt(0).toUpperCase() + order.mealType.slice(1)} Menu:</strong></p>
                        <ul>
                            {order.foodItems.map((item, index) => (
                                <li key={index}>
                                    {item.foodItemName} (Qty: {item.quantity})
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : (
                    <p>No specific menu items found for today's {order.mealType}.</p>
                )}
            </div>
            {order.addons && order.addons.length > 0 && (
                <div className="addons-details">
                    <h4>Add-ons</h4>
                    <ul>
                        {order.addons.map((item, index) => <li key={index}>{item.foodItemName} (Qty: {item.quantity})</li>)}
                    </ul>
                </div>
            )}
            <div className="assignment-section">
                <select value={selectedPerson} onChange={(e) => setSelectedPerson(e.target.value)}>
                    <option value="" disabled>Select a delivery person</option>
                    {deliveryPersons.map(person => <option key={person.id} value={person.id}>{person.name}</option>)}
                </select>
                <button onClick={handleAssignClick}>Assign</button>
            </div>
        </div>
    );
};

// Main component with logic to create new delivery documents
const AssignDelivery = () => {
    const [groupedOrders, setGroupedOrders] = useState({ breakfast: [], lunch: [], dinner: [] });
    const [deliveryPersons, setDeliveryPersons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // This data fetching logic remains the same
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const deliveryPersonsSnapshot = await getDocs(collection(db, "delivery_persons"));
                const availablePersons = deliveryPersonsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setDeliveryPersons(availablePersons);

                const subscriptionsQuery = query(collection(db, "user_subscriptions"), where("status", "==", "active"));
                const subscriptionsSnapshot = await getDocs(subscriptionsQuery);

                const ordersWithMealType = await Promise.all(
                    subscriptionsSnapshot.docs.map(async (subDoc) => {
                        const subscription = { id: subDoc.id, ...subDoc.data() };
                        let customerData = {}, addons = [], mealType = 'unknown', addonDocumentId = null;

                        // Determine meal type based on current time
                        const getCurrentMealType = () => {
                            const now = new Date();
                            const currentHour = now.getHours();
                            
                            // Breakfast: 7 AM to 12 PM (noon)
                            if (currentHour >= 7 && currentHour < 12) {
                                return 'breakfast';
                            }
                            // Lunch: 12 PM (noon) to 6 PM
                            else if (currentHour >= 12 && currentHour < 18) {
                                return 'lunch';
                            }
                            // Dinner: 6 PM to 10 PM
                            else if (currentHour >= 18 && currentHour < 22) {
                                return 'dinner';
                            }
                            // Default to breakfast for other hours (10 PM to 7 AM)
                            else {
                                return 'breakfast';
                            }
                        };
                        
                        mealType = getCurrentMealType();

                        // Get package details and food items
                        let packageData = {};
                        let foodItems = [];
                        
                        if (subscription.packageId) {
                            const packageDocRef = doc(db, "packages", subscription.packageId);
                            const packageDoc = await getDoc(packageDocRef);
                            if (packageDoc.exists()) {
                                packageData = packageDoc.data();
                                console.log('Package data fetched:', packageData);
                                console.log('Weekly menu structure:', packageData.weeklyMenu);
                                
                                // Get current day name
                                const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                const currentDay = days[new Date().getDay()];
                                
                                // Get meal type for display (capitalize first letter)
                                const mealTypeDisplay = mealType.charAt(0).toUpperCase() + mealType.slice(1);
                                
                                // Get food items for current day and meal type
                                if (packageData.weeklyMenu && Array.isArray(packageData.weeklyMenu)) {
                                    // Find the current day in the weeklyMenu array
                                    const currentDayData = packageData.weeklyMenu.find(day => day.dayName === currentDay);
                                    
                                    if (currentDayData && currentDayData.meals && Array.isArray(currentDayData.meals)) {
                                        // Find the current meal type in the meals array
                                        const currentMealData = currentDayData.meals.find(meal => 
                                            meal.mealName === mealTypeDisplay
                                        );
                                        
                                        if (currentMealData && currentMealData.items && Array.isArray(currentMealData.items)) {
                                            foodItems = currentMealData.items;
                                            console.log(`Found ${foodItems.length} food items for ${currentDay} ${mealTypeDisplay}:`, foodItems);
                                        } else {
                                            console.log(`No food items found for ${currentDay} ${mealTypeDisplay}`);
                                        }
                                    }
                                }
                            }
                        }

                        // Check if this subscription already has a delivery assigned for the current meal period
                        // Use a simpler query to avoid index requirements
                        const existingDeliveryQuery = query(
                            collection(db, "deliveries"),
                            where("originalSubscriptionId", "==", subscription.id),
                            where("mealType", "==", mealType)
                        );
                        
                        const existingDeliverySnapshot = await getDocs(existingDeliveryQuery);
                        
                        // Filter out deliveries that are not from today
                        const today = new Date();
                        const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                        
                        const todayDeliveries = existingDeliverySnapshot.docs.filter(doc => {
                            const assignedAt = doc.data().assignedAt;
                            if (!assignedAt) return false;
                            
                            const assignedDate = assignedAt.toDate ? assignedAt.toDate() : new Date(assignedAt);
                            return assignedDate >= startOfDay;
                        });
                        
                        // If there's already a delivery assigned for this meal period today, skip this subscription
                        if (todayDeliveries.length > 0) {
                            return null; // This will be filtered out later
                        }

                        if (subscription.customerId) {
                            const customerDocRef = doc(db, "customer", subscription.customerId);
                            const customerDoc = await getDoc(customerDocRef);
                            if (customerDoc.exists()) customerData = customerDoc.data();

                            // Fetch only the latest approved addon for this customer
                            const addonsQuery = query(
                                collection(db, "Addon_Approved"), 
                                where("customerId", "==", subscription.customerId),
                                where("status", "==", "approved")
                            );
                            const addonsSnapshot = await getDocs(addonsQuery);
                            if (!addonsSnapshot.empty) {
                                // Sort by requestedAt timestamp to get the latest one
                                const sortedAddons = addonsSnapshot.docs
                                    .map(doc => ({ id: doc.id, ...doc.data() }))
                                    .sort((a, b) => {
                                        const dateA = a.requestedAt?.toDate?.() || new Date(a.requestedAt);
                                        const dateB = b.requestedAt?.toDate?.() || new Date(b.requestedAt);
                                        return dateB - dateA; // Latest first
                                    });
                                
                                // Take only the latest addon
                                if (sortedAddons.length > 0) {
                                    const latestAddon = sortedAddons[0];
                                    addons = latestAddon.items || [];
                                    // Store the addon document ID for later deletion
                                    addonDocumentId = latestAddon.id;
                                }
                            }
                        }
                        
                        const orderData = { 
                            ...subscription, 
                            mealType, 
                            customer: customerData, 
                            addons,
                            packageData,
                            foodItems,
                            addonDocumentId
                        };
                        
                        console.log(`Order processed for ${orderData.customerName || 'Unknown'}:`, {
                            mealType: orderData.mealType,
                            foodItemsCount: orderData.foodItems.length,
                            packageName: orderData.packageName
                        });
                        
                        return orderData;
                    })
                );

                // Filter out null values (subscriptions that already have deliveries assigned)
                const validOrders = ordersWithMealType.filter(order => order !== null);
                
                const breakfast = validOrders.filter(o => o.mealType === 'breakfast');
                const lunch = validOrders.filter(o => o.mealType === 'lunch');
                const dinner = validOrders.filter(o => o.mealType === 'dinner');

                setGroupedOrders({ breakfast, lunch, dinner });

            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Failed to fetch data. Please check the console.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // *NEW and IMPROVED handleAssignDelivery function*
    const handleAssignDelivery = async (order, personId) => {
        const person = deliveryPersons.find(p => p.id === personId);
        if (!person) {
            alert("Could not find the selected delivery person.");
            return;
        }

        console.log(`Assigning order ${order.id} to ${person.name}`);

        // 1. Prepare the data for the new 'deliveries' collection
        const newDeliveryData = {
            // Customer Info
            customerId: order.customerId,
            customerName: order.customerName,
            customerAddress: order.customer.address || '',
            customerPhone: order.customer.phone || '',
            customerLocation: order.customer.location || '', // GeoPoint or string

            // Delivery Person Info
            deliveryPersonId: person.id,
            deliveryPersonName: person.name,

            // Order Info
            originalSubscriptionId: order.id,
            packageId: order.packageId,
            packageName: order.packageName,
            mealType: order.mealType,
            addons: order.addons || [],

            // Tracking Info
            status: "assigned", // Initial status
            assignedAt: new Date(),
            deliveredAt: null, // To be updated later
        };

        try {
            // Use a batch write to perform multiple operations atomically
            const batch = writeBatch(db);

            // 2. Create the new document in the 'deliveries' collection
            const newDeliveryRef = doc(collection(db, "deliveries")); // Auto-generates an ID
            batch.set(newDeliveryRef, newDeliveryData);
            
            // 3. Delete the addon document if it exists
            if (order.addonDocumentId) {
                const addonDocRef = doc(db, "Addon_Approved", order.addonDocumentId);
                batch.delete(addonDocRef);
            }
            
            // 4. Keep subscription as active - don't change the status
            // The subscription should remain active for the user
            // Only the delivery assignment is tracked separately

            // 5. Commit the batch
            await batch.commit();

            alert(`Order successfully assigned to ${person.name}. Subscription remains active for the user.`);

            // 5. Remove the order from the UI
            setGroupedOrders(prev => ({
                ...prev,
                [order.mealType]: prev[order.mealType].filter(o => o.id !== order.id)
            }));

        } catch (err) {
            console.error("Error assigning delivery:", err);
            alert("A failure occurred while assigning the delivery. Please try again.");
        }
    };

    if (loading) return <div className="loading-container">Loading orders...</div>;
    if (error) return <div className="error-container">{error}</div>;

    // The JSX return part does not need any changes
    return (
        <div className="assign-delivery-container">
            <h1>Assign Deliveries</h1>
            {Object.keys(groupedOrders).map(mealType => (
                <div key={mealType} className="meal-group">
                    <h2>{mealType.charAt(0).toUpperCase() + mealType.slice(1)} Orders</h2>
                    {groupedOrders[mealType].length === 0 ? (
                        <p>No {mealType} orders to assign.</p>
                    ) : (
                        <div className="order-list">
                            {groupedOrders[mealType].map(order => (
                                <OrderCard 
                                    key={order.id}
                                    order={order}
                                    deliveryPersons={deliveryPersons}
                                    onAssign={handleAssignDelivery}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default AssignDelivery;